import React, { useState, useMemo} from "react";
import "./Attendancefeerow.css";
import dummyData from "./studentDashboardData.json";
import { ResponsiveBar } from "@nivo/bar";
import { FiEye, FiEyeOff } from "react-icons/fi";

const { attendanceCard, feeCard, attendanceTrend } = dummyData;

const ATTENDANCE_COLORS = {
  present: "#a9bdfe",
  absent:  "#fecfcf",
  holiday: "#fceecb",
};

const nivoTheme = {
  axis: {
    ticks: {
      text: { fontSize: 11, fill: "#9a9ab0" },
    },
  },
  grid: {
    line: { stroke: "#f0f0f6", strokeWidth: 1 },
  },
};

// ─── Trend line layer ─────────────────────────────────────────────────────────
function TrendLineLayer({ bars, innerHeight }) {
  const seen = new Set();
  const points = [];
  bars.forEach((bar) => {
    const month = bar.data.indexValue;
    if (seen.has(month)) return;
    seen.add(month);
    const pct = bar.data.data.pct ?? 0;
    points.push({
      x: bar.x + bar.width / 2,
      y: innerHeight - (pct / 100) * innerHeight,
    });
  });
  if (points.length === 0) return null;
  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
    .join(" ");
  return (
    <g>
      <path d={path} fill="none" stroke="#27ae60" strokeWidth={2.5} />
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={3} fill="#27ae60" />
      ))}
    </g>
  );
}

const MONTH_NAMES = [
  "January", "February", "March",      "April",
  "May",     "June",     "July",       "August",
  "September","October", "November",   "December",
];

// Parse "May 2026" → { month: 5, year: 2026 }
function parseMonthYear(monthStr) {
  const parts = String(monthStr).split(" ");
  const monthIndex = MONTH_NAMES.findIndex((m) =>
    m.toLowerCase().startsWith(parts[0].toLowerCase())
  );
  return {
    month: monthIndex + 1,
    year:  parseInt(parts[1], 10),
  };
}

const parsed     = parseMonthYear(attendanceCard.month); // "May 2026"
const JSON_YEAR  = parsed.year;
const JSON_MONTH = parsed.month;

// Look up monthlyData entry for a given year + month
function getMonthData(year, month) {
  const key = `${year}-${String(month).padStart(2, "0")}`;
  return attendanceCard.monthlyData?.[key] || null;
}

// Return day status for a single day cell
function getDayStatus(day, viewYear, viewMonth) {
  const monthData = getMonthData(viewYear, viewMonth);

  if (!monthData) {
    // No data available — past months = present, future months = future
    const viewDate = new Date(viewYear, viewMonth - 1, 1);
    const jsonDate = new Date(JSON_YEAR, JSON_MONTH - 1, 1);
    return viewDate > jsonDate ? "future" : "present";
  }

  const absentSet  = new Set(monthData.absentDays);
  const holidaySet = new Set(monthData.holidayDays);
  const todayDay   = monthData.todayDay;

  if (day > todayDay)        return "future";
  if (holidaySet.has(day))   return "holiday";
  if (absentSet.has(day))    return "absent";
  return "present";
}

function buildCalendarGrid(year, month) {
  if (!year || !month || month < 1 || month > 12) return [];
  const firstDay    = new Date(year, month - 1, 1).getDay();
  const totalDays   = new Date(year, month, 0).getDate();
  const startOffset = (firstDay + 6) % 7; // Mon = 0

  const cells = [
    ...Array(startOffset).fill("empty"),
    ...Array.from({ length: totalDays }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push("empty");

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

// Dynamically compute present / absent / holiday counts from monthlyData
function getLegendCounts(year, month) {
  const monthData = getMonthData(year, month);

  if (!monthData) {
    return { presentCount: 0, absentCount: 0, holidayCount: 0, presentPct: 0 };
  }

  const absentSet  = new Set(monthData.absentDays);
  const holidaySet = new Set(monthData.holidayDays);
  const todayDay   = monthData.todayDay;

  let presentCount = 0, absentCount = 0, holidayCount = 0;

  for (let d = 1; d <= todayDay; d++) {
    if (holidaySet.has(d))      holidayCount++;
    else if (absentSet.has(d))  absentCount++;
    else                        presentCount++;
  }

  const presentPct =
    presentCount + absentCount > 0
      ? Math.round((presentCount / (presentCount + absentCount)) * 100)
      : 0;

  return { presentCount, absentCount, holidayCount, presentPct };
}

// Parse "22 April 2026" -> { year, month, day }
function parseAdmissionDate(str) {
  if (!str) return null;
  const parts = String(str).trim().split(" ");
  if (parts.length < 3) return null;
  const day = parseInt(parts[0], 10);
  const monthIndex = MONTH_NAMES.findIndex((m) =>
    m.toLowerCase().startsWith(parts[1].toLowerCase())
  );
  const year = parseInt(parts[2], 10);
  if (monthIndex === -1 || isNaN(day) || isNaN(year)) return null;
  return { year, month: monthIndex + 1, day };
}



function AttendanceCard({ attendanceData, admissionDate }) {
  const DAY_NAMES = ["M", "T", "W", "T", "F", "S", "S"];

  const admission = useMemo(() => parseAdmissionDate(admissionDate), [admissionDate]);
  const admissionDateStr = admission
    ? `${admission.year}-${String(admission.month).padStart(2, "0")}-${String(admission.day).padStart(2, "0")}`
    : null;

  // ── Parse sets from live API data ──────────────────────────────────────────
  const holidaySet = useMemo(() => {
    const s = new Set();
    (attendanceData?.holiday_list || []).forEach((d) => s.add(d));
    return s;
  }, [attendanceData]);

  const presentSet = useMemo(() => {
    const s = new Set();
    (attendanceData?.present_days_list || []).forEach((d) => s.add(d));
    return s;
  }, [attendanceData]);

  // today string "YYYY-MM-DD"
  const todayStr = useMemo(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,"0")}-${String(t.getDate()).padStart(2,"0")}`;
  }, []);

  const today = new Date();

  // Default view = current month
  const [viewYear,  setViewYear]  = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth() + 1);

  const calendar = buildCalendarGrid(viewYear, viewMonth);

  // ── Day status from live data ──────────────────────────────────────────────
  function getDayStatus(day) {
    const dateStr = `${viewYear}-${String(viewMonth).padStart(2,"0")}-${String(day).padStart(2,"0")}`;

    if (admissionDateStr && dateStr < admissionDateStr) return "disabled";
    if (dateStr > todayStr) return "future";
    if (holidaySet.has(dateStr)) return "holiday";
    if (presentSet.has(dateStr)) return "present";

    return "absent";
  }

  // ── Legend counts for current viewed month ─────────────────────────────────
  const { presentCount, absentCount, holidayCount } = useMemo(() => {
    const totalDays = new Date(viewYear, viewMonth, 0).getDate();
    let p = 0, a = 0, h = 0;

    for (let d = 1; d <= totalDays; d++) {
      const dateStr = `${viewYear}-${String(viewMonth).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
      if (admissionDateStr && dateStr < admissionDateStr) continue;
      if (dateStr > todayStr) break;
      if (holidaySet.has(dateStr))     h++;
      else if (presentSet.has(dateStr)) p++;
      else                              a++;
    }
    return { presentCount: p, absentCount: a, holidayCount: h };
  }, [viewYear, viewMonth, holidaySet, presentSet, todayStr, admissionDateStr]);

  const avgPct = attendanceData?.percentage ?? 0;

  // ── Prevent navigating before admission month ───────────────────────────────
  const isPrevDisabled = admission
    ? viewYear < admission.year || (viewYear === admission.year && viewMonth <= admission.month)
    : false;

  function prevMonth() {
    if (isPrevDisabled) return;
    if (viewMonth === 1) { setViewMonth(12); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 12) { setViewMonth(1); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  }

  return (
    <div className="att-fee-card">
      <div className="att-card-header">
        <div>
          <p className="att-title">Attendance</p>
          <div className="att-month-nav">
            <button
              className="att-nav-btn"
              onClick={prevMonth}
              disabled={isPrevDisabled}
              style={isPrevDisabled ? { opacity: 0.3, cursor: "not-allowed" } : undefined}
            >
              &#8249;
            </button>
            <span className="att-month">
              {MONTH_NAMES[viewMonth - 1]} {viewYear}
            </span>
            <button className="att-nav-btn" onClick={nextMonth}>&#8250;</button>
          </div>
        </div>
        <div className="att-pct-block">
          <span className="att-pct-value">{avgPct}%</span>
          <span className="att-pct-label">Present</span>
        </div>
      </div>

      <table className="att-cal-grid">
        <thead>
          <tr>{DAY_NAMES.map((d, i) => <th key={i}>{d}</th>)}</tr>
        </thead>
        <tbody>
          {calendar.map((week, wi) => (
            <tr key={wi}>
              {week.map((day, di) => {
                const status = day === "empty" ? "empty" : getDayStatus(day);
                return (
                  <td key={di}>
                    <span className={`att-day ${status}`}>
                      {day === "empty" ? "" : day}
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="att-legend">
        <span className="att-legend-item">
          <span className="att-legend-dot present" /> Present: {presentCount}
        </span>
        <span className="att-legend-item">
          <span className="att-legend-dot absent" /> Absent: {absentCount}
        </span>
        <span className="att-legend-item">
          <span className="att-legend-dot holiday" /> Holiday: {holidayCount}
        </span>
      </div>
    </div>
  );
}
const BAR_WIDTH_PER_MONTH = 55;
const CHART_HEIGHT = 240;
const LEFT_AXIS_WIDTH = 36;

function AttendanceGraphCard({ attendanceData }) {
  const avgPct = attendanceData?.percentage ?? 0;

  const trendData = useMemo(() => {
    return (attendanceData?.attendance_trend_list || []).map((item) => {
      return {
        month:   item.month_year,   
        present: item.present,
        absent:  item.absent,
        holiday: item.holiday,
      };
    });
  }, [attendanceData]);

  const monthCount      = trendData.length;
  const needsScroll     = monthCount > 6;
  const scrollableWidth = Math.max(monthCount * BAR_WIDTH_PER_MONTH, 300);
  const hasTrendData = monthCount > 0;

  const Tooltip = ({ id, value, indexValue, color }) => (
    <div className="att-graph-tooltip">
      <div className="att-graph-tooltip-month">{indexValue}</div>
      <div className="att-graph-tooltip-row">
        <span className="att-graph-tooltip-dot" style={{ background: color }} />
        <span className="att-graph-tooltip-label">{id}</span>
        <span className="att-graph-tooltip-value">{value}</span>
      </div>
    </div>
  );

  const commonProps = {
    data:      trendData,           // ← live data
    keys:      ["present", "absent", "holiday"],
    indexBy:   "month",
    padding:   0.35,
    groupMode: "stacked",
    colors:    ({ id }) => ATTENDANCE_COLORS[id],
    borderRadius: 2,
    enableGridX: false,
    enableGridY: true,
    gridYValues: 5,
    axisTop:    null,
    axisRight:  null,
    enableLabel: false,
    theme:      nivoTheme,
  };

  return (
    <div className="att-fee-card att-graph-card">
      {/* Header */}
      <div className="att-card-header">
        <div>
          <p className="att-title">Attendance Trend</p>
          <p className="att-month">Month wise overview</p>
        </div>
        <div className="att-pct-block">
          <span className="att-pct-value">{avgPct}%</span>
          <span className="att-pct-label">Avg. Present</span>
        </div>
      </div>
      {/* Chart */}
      {hasTrendData ? (
          needsScroll ? (
            <div style={{
              display: "flex", flexDirection: "row",
              height: CHART_HEIGHT, marginTop: 4,
              width: "100%", overflow: "hidden",
            }}>
              {/* Y-axis — sticky left */}
              <div style={{
                width: LEFT_AXIS_WIDTH, minWidth: LEFT_AXIS_WIDTH,
                height: CHART_HEIGHT, flexShrink: 0,
                pointerEvents: "none", overflow: "hidden",
              }}>
                <div style={{ width: LEFT_AXIS_WIDTH, height: CHART_HEIGHT }}>
                  <ResponsiveBar
                    {...commonProps}
                    margin={{ top: 10, right: 0, bottom: 30, left: LEFT_AXIS_WIDTH }}
                    axisBottom={null}
                    axisLeft={{ tickSize: 0, tickPadding: 6 }}
                    isInteractive={false}
                    animate={false}
                    layers={["grid", "axes"]}
                  />
                </div>
              </div>
              <div style={{
                flex: 1, minWidth: 0,
                height: CHART_HEIGHT,
                overflowX: "auto", overflowY: "hidden",
                scrollbarWidth: "thin",
                scrollbarColor: "#cbd5e0 #f0f0f6",
              }}
                className="att-trend-scroll-pane"
              >
                <div style={{ width: scrollableWidth, height: CHART_HEIGHT }}>
                  <ResponsiveBar
                    {...commonProps}
                    margin={{ top: 10, right: 10, bottom: 30, left: 0 }}
                    axisBottom={{ tickSize: 0, tickPadding: 8 }}
                    axisLeft={null}
                    isInteractive={true}
                    animate={false}
                    tooltip={Tooltip}
                    layers={["grid", "axes", "bars", "markers", "legends", "annotations"]}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div style={{ height: CHART_HEIGHT, marginTop: 4 }}>
              <ResponsiveBar
                {...commonProps}
                margin={{ top: 10, right: 10, bottom: 30, left: LEFT_AXIS_WIDTH }}
                axisBottom={{ tickSize: 0, tickPadding: 8 }}
                axisLeft={{ tickSize: 0, tickPadding: 8 }}
                animate={true}
                motionConfig="gentle"
                tooltip={Tooltip}
                layers={["grid", "axes", "bars", "markers", "legends", "annotations"]}
              />
            </div>
          )
        ) : (
          <div className="att-graph-no-data" style={{ height: CHART_HEIGHT }}>
            <p className="stat-card-sublabel">No data available</p>
          </div>
        )}

      {/* Legend */}
      <div className="att-legend">
        <span className="att-legend-item">
          <span className="att-legend-dot present" />Present
        </span>
        <span className="att-legend-item">
          <span className="att-legend-dot absent" />Absent
        </span>
        <span className="att-legend-item">
          <span className="att-legend-dot holiday" />Holiday
        </span>
      </div>
    </div>
  );
}


// function FeeStatusCard() {
//   const { subtitle, summary, annualProgress, items } = feeCard;

//   const STATUS_ORDER = { upcoming: 0, partial: 1, due: 2, overdue: 3 };
//   const sortedItems = useMemo(
//     () =>
//       [...items].sort(
//         (a, b) => (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99)
//       ),
//     [items]
//   );

//   const [showDetails, setShowDetails] = useState(false);
//   const isHideEnabled = 1;

//   return (
//     <div className="att-fee-card fee-flip-container">
//       <div className={`fee-flip-inner ${showDetails ? "flipped" : ""}`}>
//         {/* ── FRONT FACE ── */}
//         <div className="fee-flip-front">
//           <div className="fee-card-header">
//             <div>
//               <h3 className="fee-title">Fee Status</h3>
//               <p className="fee-subtitle">{subtitle}</p>
//             </div>
//           </div>
//           <div className="fee-empty-state">
//             {/* <p className="fee-empty-text">Tap to view payment details</p> */}
//             <button
//               className="fee-view-all-btn"
//               onClick={() => setShowDetails(true)}
//               disabled={isHideEnabled === 0}
//             >
//               Hide
//             </button>
//           </div>
//         </div>

//         {/* ── BACK FACE ── */}
//         <div className="fee-flip-back">
//           <div className="fee-card-header">
//             <div>
//               <h3 className="fee-title">Fee Status</h3>
//               <p className="fee-subtitle">{subtitle}</p>
//             </div>
//             <FiEye
//               className="fee-eye-icon"
//               size={16}
//               onClick={() => setShowDetails(false)}
//               role="button"
//               aria-label="Back to summary"
//               title="Back to summary"
//           />
//           </div>

//           <div className="fee-summary-row">
//             <div className="fee-chip paid">
//               <span className="fee-chip-label"><span className="chip-dot" /> Total Paid</span>
//               <span className="fee-chip-value">{summary.totalPaid}</span>
//             </div>
//             <div className="fee-chip pending">
//               <span className="fee-chip-label"><span className="chip-dot" /> Pending</span>
//               <span className="fee-chip-value">{summary.pending}</span>
//             </div>
//             <div className="fee-chip due">
//               <span className="fee-chip-label"><span className="chip-dot" /> Next Due</span>
//               <span className="fee-chip-value">{summary.nextDue}</span>
//             </div>
//           </div>

//           <div className="fee-annual-row">
//             <span className="fee-annual-label">{annualProgress.label}</span>
//             <span className="fee-annual-pct">{annualProgress.pct}% Paid</span>
//           </div>
//           <div className="fee-annual-bar-track">
//             <div
//               className="fee-annual-bar-fill"
//               style={{ width: `${annualProgress.pct}%` }}
//             />
//           </div>

//           <div className="fee-items-list">
//             {sortedItems.map((item, i) => (
//               <div key={i} className="fee-line-item">
//                 <div className="fee-line-left">
//                   <span className="fee-line-name">{item.name}</span>
//                   <span className="fee-line-date">N/A</span>
//                 </div>
//                 <div className="fee-line-right">
//                   <span className="fee-line-amount">{item.amount}</span>
//                   <span className={`fee-status-badge ${item.status}`}>
//                     {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
//                   </span>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }

function FeeStatusCard() {
  const { subtitle, summary, annualProgress, items } = feeCard;

  const STATUS_ORDER = { upcoming: 0, partial: 1, due: 2, overdue: 3 };
  const sortedItems = useMemo(
    () =>
      [...items].sort(
        (a, b) => (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99)
      ),
    [items]
  );

  const [showDetails, setShowDetails] = useState(false);
  const isHideEnabled = 1;

  return (
    <div className="att-fee-card fee-flip-container">
      <div className={`fee-flip-inner ${showDetails ? "flipped" : ""}`}>
        {/* ── FRONT FACE ── */}
        <div className="fee-flip-front">
          <div className="fee-card-header">
            <div>
              <h3 className="fee-title">Fee Status</h3>
              <p className="fee-subtitle">{subtitle}</p>
            </div>
            {isHideEnabled !== 0 && (
              <FiEyeOff
                className="fee-eye-icon"
                size={16}
                onClick={() => setShowDetails(true)}
                role="button"
                aria-label="View fee details"
                title="View fee details"
              />
            )}
          </div>
          <div className="fee-empty-state">
            {isHideEnabled === 0 ? (
              <p className="fee-empty-text">  Please contact the administrator to view the fee details.</p>
            ) : (
              <p className="fee-empty-text">Tap the eye icon to view payment details</p>
            )}
          </div>
        </div>

        {/* ── BACK FACE ── */}
        <div className="fee-flip-back">
          <div className="fee-card-header">
            <div>
              <h3 className="fee-title">Fee Status</h3>
              <p className="fee-subtitle">{subtitle}</p>
            </div>
            <FiEye
              className="fee-eye-icon"
              size={16}
              onClick={() => setShowDetails(false)}
              role="button"
              aria-label="Back to summary"
              title="Back to summary"
            />
          </div>

          <div className="fee-summary-row">
            <div className="fee-chip paid">
              <span className="fee-chip-label"><span className="chip-dot" /> Total Paid</span>
              <span className="fee-chip-value">{summary.totalPaid}</span>
            </div>
            <div className="fee-chip pending">
              <span className="fee-chip-label"><span className="chip-dot" /> Pending</span>
              <span className="fee-chip-value">{summary.pending}</span>
            </div>
            <div className="fee-chip due">
              <span className="fee-chip-label"><span className="chip-dot" /> Next Due</span>
              <span className="fee-chip-value">{summary.nextDue}</span>
            </div>
          </div>

          <div className="fee-annual-row">
            <span className="fee-annual-label">{annualProgress.label}</span>
            <span className="fee-annual-pct">{annualProgress.pct}% Paid</span>
          </div>
          <div className="fee-annual-bar-track">
            <div
              className="fee-annual-bar-fill"
              style={{ width: `${annualProgress.pct}%` }}
            />
          </div>

          <div className="fee-items-list">
            {sortedItems.map((item, i) => (
              <div key={i} className="fee-line-item">
                <div className="fee-line-left">
                  <span className="fee-line-name">{item.name}</span>
                  <span className="fee-line-date">N/A</span>
                </div>
                <div className="fee-line-right">
                  <span className="fee-line-amount">{item.amount}</span>
                  <span className={`fee-status-badge ${item.status}`}>
                    {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


export default function AttendanceFeeRow({ attendanceData, admissionDate  }) {
  
  return (
    <div className="att-fee-row">
      <AttendanceCard attendanceData={attendanceData} admissionDate={admissionDate} />
       <AttendanceGraphCard attendanceData={attendanceData} /> 
      <FeeStatusCard />
    </div>
  );
}






// function FeeStatusCard() {
//   const { subtitle, summary, annualProgress, items } = feeCard;

//   const STATUS_ORDER = { upcoming: 0, partial: 1, due: 2, overdue: 3 };
//   const sortedItems = [...items].sort(
//     (a, b) => (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99)
//   );

//   return (
//     <div className="att-fee-card">
//       <div className="fee-card-header">
//         <h3 className="fee-title">Fee Status</h3>
//       </div>
//       <p className="fee-subtitle">{subtitle}</p>

//       <div className="fee-summary-row">
//         <div className="fee-chip paid">
//           <span className="fee-chip-label">
//             <span className="chip-dot" /> Total Paid
//           </span>
//           <span className="fee-chip-value">{summary.totalPaid}</span>
//         </div>
//         <div className="fee-chip pending">
//           <span className="fee-chip-label">
//             <span className="chip-dot" /> Pending
//           </span>
//           <span className="fee-chip-value">{summary.pending}</span>
//         </div>
//         <div className="fee-chip due">
//           <span className="fee-chip-label">
//             <span className="chip-dot" /> Next Due
//           </span>
//           <span className="fee-chip-value">{summary.nextDue}</span>
//         </div>
//       </div>

//       <div className="fee-annual-row">
//         <span className="fee-annual-label">{annualProgress.label}</span>
//         <span className="fee-annual-pct">{annualProgress.pct}% Paid</span>
//       </div>
//       <div className="fee-annual-bar-track">
//         <div
//           className="fee-annual-bar-fill"
//           style={{ width: `${annualProgress.pct}%` }}
//         />
//       </div>

//       <div className="fee-items-list">
//         {sortedItems.map((item, i) => (
//           <div key={i} className="fee-line-item">
//             <div className="fee-line-left">
//               <span className="fee-line-name">{item.name}</span>
//               <span className="fee-line-date">N/A</span>
//             </div>
//             <div className="fee-line-right">
//               <span className="fee-line-amount">{item.amount}</span>
//               <span className={`fee-status-badge ${item.status}`}>
//                 {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
//               </span>
//             </div>
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// }
